import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { HEALTH } from "@/lib/meta";
import { DailyWork } from "@/components/DailyWork";
import { PendingWorkTable } from "@/components/PendingWorkTable";
import { InProgressNow } from "@/components/InProgressNow";
import { UpcomingFestivals } from "@/components/UpcomingFestivals";
import { Card } from "@/components/ui/card";
import { Loader2, Building2, ChevronRight, Camera, Image, Wallet } from "lucide-react";

function Stat({ v, l, tone = "zinc" }) {
  const map = { zinc: "text-zinc-900", green: "text-emerald-600", orange: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="text-center">
      <div className={`metric-value text-lg font-semibold ${map[tone]}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-400">{l}</div>
    </div>
  );
}

export default function AccountWorkspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["ws-my-clients"], queryFn: async () => (await api.get("/workspace/my-clients")).data });

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="space-y-6" data-testid="account-workspace">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">My Accounts</h1>
          <p className="mt-1 text-sm text-zinc-500">Hi {user?.name?.split(" ")[0]} — {data.clients.length} client{data.clients.length === 1 ? "" : "s"} under your management.</p>
        </div>
        {user?.can_view_payments && (
          <button onClick={() => navigate("/payments")} className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium hover:bg-zinc-50" data-testid="account-payments-link">
            <Wallet className="h-4 w-4 text-zinc-500" /> Payments
          </button>
        )}
      </div>

      <InProgressNow />

      <UpcomingFestivals days={45} showCoverage />

      <PendingWorkTable />

      {data.clients.length === 0 ? (
        <Card className="rounded-lg border-dashed border-zinc-300 py-12 text-center text-sm text-zinc-400">No clients assigned to you yet.</Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.clients.map((c) => (
            <button key={c.client_id} onClick={() => navigate(`/clients/${c.client_id}`)} data-testid={`account-client-${c.client_id}`} className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-100 text-zinc-500"><Building2 className="h-4 w-4" /></div>
                  <div className="font-heading font-semibold text-zinc-900">{c.client_name}</div>
                </div>
                <Badge color={c.health} dot>{HEALTH[c.health]?.label || c.health_label}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-1 sm:grid-cols-6">
                <Stat v={c.planned} l="Planned" tone="blue" />
                <Stat v={c.ready_for_approval} l="Ready" tone="blue" />
                <Stat v={c.approved} l="Approved" tone="green" />
                <Stat v={c.posted} l="Posted" tone="green" />
                <Stat v={c.pending} l="Pending" tone="orange" />
                <Stat v={c.delayed} l="Delayed" tone="red" />
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-2 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> {c.stories} stories</span>
                {c.upcoming_shoot && <span className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Shoot {fmtDate(c.upcoming_shoot.date)}</span>}
                <span className="flex items-center gap-1 font-medium text-zinc-700">Open <ChevronRight className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      )}

      <DailyWork />
    </div>
  );
}
